import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Fab,
  Card,
  CardContent,
  Grid,
  Snackbar,
  useTheme,
  alpha,
  Fade,
  LinearProgress,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Engineering as EngineeringIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

const EquipmentList = () => {
  const { companyId, departmentId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  
  // State management
  const [view, setView] = useState('departments'); // 'departments' or 'equipment'
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [equipmentToDelete, setEquipmentToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({
    id: '',
    serial_number: '',
    equipment_name: '',
    equipment_number: '',
    make: '',
    year_of_installation: '',
    rated_capacity: '',
    remarks: '',
  });

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setDepartments(data || []);
      setFilteredDepartments(data || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError('Failed to load departments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipment = async () => {
    if (!selectedDepartment) return;
    
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('equipment')
        .select('*')
        .eq('company_id', companyId)
        .eq('department_id', selectedDepartment.id)
        .order('serial_number', { ascending: true });

      if (error) throw error;
      setEquipment(data || []);
      setFilteredEquipment(data || []);
    } catch (err) {
      console.error('Error fetching equipment:', err);
      setError('Failed to load equipment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (companyError) throw companyError;
      setCompanyName(companyData?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchCompanyData();
  }, [companyId]);

  // Handle department ID from URL
  useEffect(() => {
    if (departmentId && departments.length > 0) {
      const dept = departments.find(d => d.id === departmentId);
      if (dept) {
        setSelectedDepartment(dept);
        setView('equipment');
      }
    } else if (!departmentId) {
      setView('departments');
      setSelectedDepartment(null);
    }
  }, [departmentId, departments]);

  useEffect(() => {
    if (selectedDepartment && view === 'equipment') {
      fetchEquipment();
    }
  }, [selectedDepartment, view]);

  // Filter based on current view and search term
  useEffect(() => {
    if (view === 'departments') {
      if (!searchTerm.trim()) {
        setFilteredDepartments(departments);
        return;
      }
      const searchLower = searchTerm.toLowerCase();
      const filtered = departments.filter(dept => 
        dept.name?.toLowerCase().includes(searchLower) ||
        dept.description?.toLowerCase().includes(searchLower)
      );
      setFilteredDepartments(filtered);
      setPage(0);
    } else {
      if (!searchTerm.trim()) {
        setFilteredEquipment(equipment);
        return;
      }
      const searchLower = searchTerm.toLowerCase();
      const filtered = equipment.filter(item => 
        item.equipment_name?.toLowerCase().includes(searchLower) ||
        item.equipment_number?.toLowerCase().includes(searchLower) ||
        item.make?.toLowerCase().includes(searchLower) ||
        item.rated_capacity?.toLowerCase().includes(searchLower) ||
        item.remarks?.toLowerCase().includes(searchLower) ||
        item.serial_number?.toString().includes(searchLower)
      );
      setFilteredEquipment(filtered);
      setPage(0);
    }
  }, [searchTerm, equipment, departments, view]);

  const resetFormData = () => {
    setFormData({
      id: '',
      serial_number: '',
      equipment_name: '',
      equipment_number: '',
      make: '',
      year_of_installation: '',
      rated_capacity: '',
      remarks: '',
    });
  };

  const handleOpenDialog = async (equipmentItem = null) => {
    if (equipmentItem) {
      setEditMode(true);
      setFormData({
        id: equipmentItem.id,
        serial_number: equipmentItem.serial_number,
        equipment_name: equipmentItem.equipment_name || '',
        equipment_number: equipmentItem.equipment_number || '',
        make: equipmentItem.make || '',
        year_of_installation: equipmentItem.year_of_installation || '',
        rated_capacity: equipmentItem.rated_capacity || '',
        remarks: equipmentItem.remarks || '',
      });
    } else {
      setEditMode(false);
      resetFormData();
      
      // Get next serial number
      if (selectedDepartment) {
        try {
          const { data, error } = await supabase
            .rpc('get_next_equipment_serial_number', {
              p_company_id: companyId,
              p_department_id: selectedDepartment.id
            });
          
          if (!error && data) {
            setFormData(prev => ({ ...prev, serial_number: data }));
          }
        } catch (err) {
          console.error('Error getting next serial number:', err);
        }
      }
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    resetFormData();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.equipment_name.trim()) {
      setError('Equipment name is required');
      return;
    }

    if (!selectedDepartment) {
      setError('Please select a department');
      return;
    }

    // Check permissions before proceeding
    if (!hasPermission('locations.equipment_list.edit', 'write')) {
      setError('You do not have permission to create or edit equipment.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const equipmentData = {
        equipment_name: formData.equipment_name.trim(),
        equipment_number: formData.equipment_number.trim() || null,
        make: formData.make.trim() || null,
        year_of_installation: formData.year_of_installation ? parseInt(formData.year_of_installation) : null,
        rated_capacity: formData.rated_capacity.trim() || null,
        remarks: formData.remarks.trim() || null,
        serial_number: parseInt(formData.serial_number),
        company_id: companyId,
        department_id: selectedDepartment.id
      };

      let result;
      if (editMode) {
        result = await supabase
          .from('equipment')
          .update({ ...equipmentData, updated_at: new Date().toISOString() })
          .eq('id', formData.id)
          .select()
          .single();
      } else {
        result = await supabase
          .from('equipment')
          .insert(equipmentData)
          .select()
          .single();
      }

      if (result.error) throw result.error;

      await fetchEquipment();
      handleCloseDialog();
      
      setSnackbar({
        open: true,
        message: `Equipment ${editMode ? 'updated' : 'added'} successfully`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving equipment:', err);
      setError(`Failed to ${editMode ? 'update' : 'create'} equipment. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (equipmentItem) => {
    setEquipmentToDelete(equipmentItem);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!equipmentToDelete) return;

    // Check permissions before proceeding
    if (!hasPermission('locations.equipment_list.delete', 'delete')) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to delete equipment.',
        severity: 'error'
      });
      setDeleteConfirmOpen(false);
      setEquipmentToDelete(null);
      return;
    }

    try {
      const { error } = await supabase
        .from('equipment')
        .delete()
        .eq('id', equipmentToDelete.id);

      if (error) throw error;

      await fetchEquipment();
      
      setSnackbar({
        open: true,
        message: 'Equipment deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error deleting equipment: ${err.message}`,
        severity: 'error'
      });
    }

    setDeleteConfirmOpen(false);
    setEquipmentToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setEquipmentToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Department selection handlers
  const handleSelectDepartment = (department) => {
    navigate(`/company/${companyId}/equipment-list/${department.id}`);
    setSearchTerm('');
    setPage(0);
  };

  const handleBackToDepartments = () => {
    navigate(`/company/${companyId}/equipment-list`);
    setSearchTerm('');
    setPage(0);
  };

  // Permission check
  if (!hasPermission('locations.equipment_list.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <EngineeringIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Equipment List Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Equipment List. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }
  
  // Check edit permissions  
  const canEdit = hasPermission('locations.equipment_list.edit', 'write');

  if (loading && departments.length === 0) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#3F51B5', 0.15)}`,
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#3F51B5', 0.1),
                mb: 2
              }}>
                <EngineeringIcon sx={{ fontSize: 48, color: '#3F51B5' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#3F51B5',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Departments
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing department information and equipment data...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#3F51B5',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Box>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Header Section */}
          <Box sx={{ mb: 3 }}>
            {/* Enhanced Page Header */}
            <Card sx={{
              borderRadius: 3,
              mt: 5,
              mb: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              borderTop: '3px solid #3F51B5',
              background: theme.palette.grey[100],
              backdropFilter: 'blur(20px)',
              boxShadow: 'none',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 20px ${alpha('#3F51B5', 0.15)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                  {view === 'equipment' && (
                    <Tooltip title="Back to Departments">
                      <IconButton
                        onClick={handleBackToDepartments}
                        sx={{
                          bgcolor: 'white',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          borderRadius: 2,
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            transform: 'scale(1.05)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                          }
                        }}
                      >
                        <ArrowBackIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Box sx={{
                    p: 2,
                    borderRadius: 3,
                    background: `linear-gradient(${alpha('#3F51B5', 0.08)}, ${alpha('#3F51B5', 0.04)})`,
                    border: `1px solid ${alpha('#3F51B5', 0.15)}`
                  }}>
                    <EngineeringIcon sx={{ fontSize: 32, color: '#3F51B5' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{
                      fontWeight: 500,
                      color: '#3F51B5',
                      mb: 1,
                      fontSize: '2rem',
                      letterSpacing: '-0.5px'
                    }}>
                      {view === 'departments' ? 'Equipment by Department' : `${selectedDepartment?.name} - Equipment`}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      {companyName}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary', 
                  maxWidth: 700, 
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}>
                  {view === 'departments' 
                    ? 'Select a department to view and manage its equipment inventory.'
                    : 'Manage equipment inventory, track equipment details, and maintain comprehensive records.'}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Filters */}
          <Card sx={{
            height: 100,
            mb: 2,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  placeholder={view === 'departments' ? 'Search departments...' : 'Search equipment...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: 280,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      height: 40,
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      py: 1.25
                    }
                  }}
                />
                
                <Box sx={{ flex: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {view === 'departments' 
                    ? `${filteredDepartments.length} department${filteredDepartments.length !== 1 ? 's' : ''}`
                    : `${filteredEquipment.length} equipment item${filteredEquipment.length !== 1 ? 's' : ''}`
                  }
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Departments View */}
          {view === 'departments' && (
            departments.length === 0 ? (
              <Paper 
                sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 4,
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
              <Box sx={{ 
                p: 2.5, 
                borderRadius: 3, 
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backdropFilter: 'blur(10px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                mb: 3,
                width: 'fit-content',
                mx: 'auto'
              }}>
                <EngineeringIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
              </Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                No Departments Available
              </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto', fontWeight: 500 }}>
                  Please create departments in Settings to manage equipment.
                </Typography>
              </Paper>
            ) : (
              <Paper sx={{ 
                borderRadius: 3, 
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '@keyframes slideInUp': {
                  '0%': {
                    transform: 'translateY(100%)',
                    opacity: 0,
                  },
                  '100%': {
                    transform: 'translateY(0)',
                    opacity: 1,
                  }
                },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}>
                <TableContainer sx={{ overflow: 'visible' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 1.5,
                            px: 2.5
                          }}
                        >
                          Department Name
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(rowsPerPage > 0
                        ? filteredDepartments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        : filteredDepartments
                      ).map((department) => (
                        <TableRow 
                          key={department.id} 
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              transition: 'all 0.2s ease',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2,
                              px: 2.5
                            }
                          }}
                          onClick={() => handleSelectDepartment(department)}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  fontWeight: 500,
                                  color: 'text.primary',
                                  fontSize: '0.95rem'
                                }}
                              >
                                {department.name}
                              </Typography>
                              <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Box sx={{ 
                  borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                  borderRadius: '0 0 12px 12px'
                }}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredDepartments.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ 
                      px: 3,
                      py: 2,
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        color: theme.palette.text.primary
                      },
                      '& .MuiTablePagination-select': {
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        background: 'white',
                        '&:hover': {
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        }
                      },
                      '& .MuiTablePagination-actions': {
                        '& .MuiIconButton-root': {
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          borderRadius: 2,
                          margin: '0 2px',
                          background: 'white',
                          color: theme.palette.primary.main,
                          fontWeight: 500,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: theme.palette.primary.main,
                            color: 'white',
                            transform: 'translateY(-1px)',
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                          },
                          '&.Mui-disabled': {
                            background: alpha(theme.palette.grey[300], 0.5),
                            color: theme.palette.grey[500],
                            border: `1px solid ${theme.palette.grey[300]}`,
                            transform: 'none',
                            boxShadow: 'none'
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </Paper>
            )
          )}

          {/* Equipment View */}
          {view === 'equipment' && (
            filteredEquipment.length === 0 && equipment.length === 0 ? (
            <Paper 
              sx={{
                textAlign: 'center',
                py: 8,
                px: 4,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Box sx={{ 
                p: 2.5, 
                borderRadius: 3, 
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backdropFilter: 'blur(10px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                mb: 3,
                width: 'fit-content',
                mx: 'auto'
              }}>
                <EngineeringIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
              </Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                No Equipment Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto', fontWeight: 500 }}>
                Add your first equipment item to start tracking department equipment inventory.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                disabled={!canEdit}
                sx={{ 
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                  }
                }}
              >
                Add First Equipment
              </Button>
            </Paper>
          ) : (
            <Paper sx={{ 
              borderRadius: 3, 
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}>
              <TableContainer sx={{ overflow: 'visible' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 1.5,
                          px: 2.5,
                          width: 80
                        }}
                      >
                        S.No
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 1.5,
                          px: 2.5
                        }}
                      >
                        Equipment Name and Number
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 1.5,
                          px: 2.5
                        }}
                      >
                        Make of Equipment
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 1.5,
                          px: 2.5
                        }}
                      >
                        Year of Installation
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 1.5,
                          px: 2.5
                        }}
                      >
                        Rated Capacity / Rating
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 1.5,
                          px: 2.5
                        }}
                      >
                        Remarks If Any
                      </TableCell>
                      <TableCell 
                        align="center"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 1.5,
                          px: 2.5,
                          width: 120
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(rowsPerPage > 0
                      ? filteredEquipment.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      : filteredEquipment
                    ).map((equipmentItem) => (
                      <TableRow 
                        key={equipmentItem.id} 
                        hover
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            bgcolor: 'grey.50',
                            transform: 'translateX(4px)',
                            transition: 'all 0.2s ease',
                            boxShadow: 'inset 4px 0 0 black'
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2,
                            px: 2.5
                          }
                        }}
                      >
                        <TableCell>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500,
                              color: 'text.primary',
                              fontSize: '0.95rem'
                            }}
                          >
                            {equipmentItem.serial_number}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500,
                              color: 'text.primary',
                              fontSize: '0.95rem'
                            }}
                          >
                            {equipmentItem.equipment_name}
                          </Typography>
                          {equipmentItem.equipment_number && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {equipmentItem.equipment_number}
                            </Typography>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: 'text.primary',
                              fontSize: '0.9rem'
                            }}
                          >
                            {equipmentItem.make || '—'}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: 'text.primary',
                              fontSize: '0.9rem'
                            }}
                          >
                            {equipmentItem.year_of_installation || '—'}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: 'text.primary',
                              fontSize: '0.9rem'
                            }}
                          >
                            {equipmentItem.rated_capacity || '—'}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: 'text.primary',
                              fontSize: '0.9rem'
                            }}
                          >
                            {equipmentItem.remarks || '—'}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="center">
                          <Tooltip title={canEdit ? "Edit" : "No permission to edit"}>
                            <span>
                              <IconButton 
                                size="small" 
                                disabled={!canEdit}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDialog(equipmentItem);
                                }}
                                sx={{
                                  mr: 1,
                                  color: 'text.primary',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                  borderRadius: 2,
                                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                  '&:hover:not(:disabled)': {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    transform: 'scale(1.05)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                  },
                                  '&.Mui-disabled': {
                                    color: alpha(theme.palette.text.primary, 0.3),
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`,
                                    cursor: 'not-allowed'
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={hasPermission('locations.equipment_list.delete', 'delete') ? "Delete" : "No permission to delete"}>
                            <span>
                              <IconButton 
                                size="small" 
                                disabled={!hasPermission('locations.equipment_list.delete', 'delete')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(equipmentItem);
                                }}
                                sx={{
                                  color: 'error.main',
                                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                  borderRadius: 2,
                                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                  '&:hover:not(:disabled)': {
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    transform: 'scale(1.05)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`
                                  },
                                  '&.Mui-disabled': {
                                    color: alpha(theme.palette.error.main, 0.3),
                                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                    cursor: 'not-allowed'
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ 
                borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                borderRadius: '0 0 12px 12px'
              }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredEquipment.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ 
                    px: 3,
                    py: 2,
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      color: theme.palette.text.primary
                    },
                    '& .MuiTablePagination-select': {
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      background: 'white',
                      '&:hover': {
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    },
                    '& .MuiTablePagination-actions': {
                      '& .MuiIconButton-root': {
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        borderRadius: 2,
                        margin: '0 2px',
                        background: 'white',
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: theme.palette.primary.main,
                          color: 'white',
                          transform: 'translateY(-1px)',
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        },
                        '&.Mui-disabled': {
                          background: alpha(theme.palette.grey[300], 0.5),
                          color: theme.palette.grey[500],
                          border: `1px solid ${theme.palette.grey[300]}`,
                          transform: 'none',
                          boxShadow: 'none'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          )
          )}

          {view === 'equipment' && (
            <Tooltip title={canEdit ? "Add Equipment" : "No permission to add equipment"}>
              <span>
                <Fab
                  color="primary"
                  aria-label="add"
                  disabled={!canEdit}
                  sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 10,
                    width: 100,
                    height: 100,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover:not(:disabled)': {
                      transform: 'translateY(-1px) scale(1.02)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                    },
                    '&.Mui-disabled': {
                      bgcolor: alpha(theme.palette.grey[400], 0.3),
                      color: alpha(theme.palette.text.primary, 0.3),
                      cursor: 'not-allowed'
                    }
                  }}
                  onClick={() => handleOpenDialog()}
                >
                  <AddIcon sx={{ fontSize: 32 }} />
                </Fab>
              </span>
            </Tooltip>
          )}

          {/* Form Dialog */}
          <Dialog 
            open={dialogOpen} 
            onClose={handleCloseDialog} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
              sx: { 
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
              }
            }}
          >
            <DialogTitle sx={{ 
              p: 3,
              borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              fontSize: '1.25rem',
              background: 'white'
            }}>
              {editMode ? 'Edit Equipment' : 'Add New Equipment'}
            </DialogTitle>

            <DialogContent sx={{ 
              p: 0,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`
            }}>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
                  <TextField
                    label="S.No"
                    fullWidth
                    type="number"
                    value={formData.serial_number}
                    onChange={(e) => handleInputChange('serial_number', e.target.value)}
                    required
                    disabled={editMode}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' },
                        '&:hover': {
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      }
                    }}
                  />
                  <TextField
                    label="Equipment Name"
                    fullWidth
                    value={formData.equipment_name}
                    onChange={(e) => handleInputChange('equipment_name', e.target.value)}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' },
                        '&:hover': {
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      }
                    }}
                  />
                  <TextField
                    label="Equipment Number"
                    fullWidth
                    value={formData.equipment_number}
                    onChange={(e) => handleInputChange('equipment_number', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' }
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      }
                    }}
                  />
                  <TextField
                    label="Make of Equipment"
                    fullWidth
                    value={formData.make}
                    onChange={(e) => handleInputChange('make', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' }
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      }
                    }}
                  />
                  <TextField
                    label="Year of Installation"
                    fullWidth
                    type="number"
                    value={formData.year_of_installation}
                    onChange={(e) => handleInputChange('year_of_installation', e.target.value)}
                    InputProps={{
                      inputProps: { min: 0 }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' }
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      }
                    }}
                  />
                  <TextField
                    label="Rated Capacity / Rating"
                    fullWidth
                    value={formData.rated_capacity}
                    onChange={(e) => handleInputChange('rated_capacity', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' }
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      }
                    }}
                  />
                  <TextField
                    label="Remarks If Any"
                    fullWidth
                    multiline
                    rows={3}
                    value={formData.remarks}
                    onChange={(e) => handleInputChange('remarks', e.target.value)}
                    sx={{ 
                      gridColumn: { md: '1 / -1' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' }
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      }
                    }}
                  />
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ 
              p: 3, 
              borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              background: 'white',
              gap: 2
            }}>
              <Button 
                onClick={handleCloseDialog} 
                disabled={submitting}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={submitting || !formData.equipment_name.trim()}
                sx={{ 
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                  }
                }}
              >
                {submitting ? 'Saving...' : editMode ? 'Update Equipment' : 'Add Equipment'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteConfirmOpen}
            onClose={handleCancelDelete}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: { 
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
              }
            }}
          >
            <DialogTitle sx={{ 
              pb: 2,
              fontWeight: 500,
              fontSize: '1.25rem'
            }}>
              Confirm Delete
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ fontWeight: 500 }}>
                Are you sure you want to delete "{equipmentToDelete?.equipment_name}"? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, gap: 1 }}>
              <Button
                onClick={handleCancelDelete}
                variant="outlined"
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                variant="contained"
                color="error"
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.error.main, 0.25)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.error.main, 0.35)}`
                  }
                }}
              >
                Delete Equipment
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert 
              onClose={handleCloseSnackbar} 
              severity={snackbar.severity}
              sx={{ 
                width: '100%',
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
                '& .MuiAlert-message': {
                  fontWeight: 500,
                  fontSize: '0.9rem'
                }
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>

        </Container>
      </Box>
    </Fade>
  );
};

export default EquipmentList;